/**
 * Global error capture for the TV.
 *
 * Before this, the TV had no `error` / `unhandledrejection` listener. A
 * render crash reached the console through SelfHealBoundary, but a failure
 * in a timer, a promise or an event handler usually did not. The log
 * shipper only sends what goes through `console.*`, so those errors never
 * left the panel.
 *
 * Two destinations, because they answer different questions:
 * - `console.error` feeds logBuffer.ts and logShipper.ts: "what happened on
 *   THIS screen" (screen-scoped, 14 days, the device dialog's Device log).
 * - the shared reporter (src/shared/clientErrors.ts): "which errors are
 *   happening across the fleet, and since which release" (grouped, 30 days).
 */
import {
  describeThrown,
  installClientErrorReporting,
  type ErrorReporter,
} from "@shared/clientErrors";

let installed = false;

export function installUncaughtErrorCapture(): ErrorReporter | null {
  if (installed || typeof window === "undefined") return null;
  installed = true;

  window.addEventListener("error", (e: ErrorEvent) => {
    if (!e.error && (!e.message || e.message === "Script error.")) return;
    const { message, stack } = describeThrown(e.error ?? e.message);
    console.error(`[uncaught] ${message}`, stack ?? "");
  });
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const { message, stack } = describeThrown(e.reason);
    console.error(`[unhandledrejection] ${message}`, stack ?? "");
  });

  return installClientErrorReporting(
    "tv",
    (import.meta.env?.VITE_APP_VERSION as string | undefined) ?? "dev"
  );
}
